import { App, Input, Modal } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CreateVendorErrorCodeRequestDto,
  UpdateVendorErrorCodeRequestDto,
  VendorErrorCodeResponseDto,
} from '@evimo/client/models';
import { UnresolvedVendorErrorCodeStatus } from '@evimo/types/enums/unresolved-vendor-error-code-status';
import { VendorErrorSource } from '@evimo/types/enums/vendor-error-source';
import type {
  VendorErrorCodeDrawerMode,
  VendorErrorCodeFormValues,
} from '~/app/components/errorManagement/ErrorManagementPage/types';
import {
  buildCreateRequestPayload,
  buildUpdateRequestPayload,
  isConflictError,
} from '~/app/components/errorManagement/ErrorManagementPage/request-payload';
import { useDebouncedValue } from '~/app/hooks/useDebouncedValue';
import { ManufacturerCatalogApi } from '~/app/services/knowledgeBase/catalog.api';
import { assetTypesApi } from '~/app/services/knowledgeBase/api-instances';
import { VendorErrorCodeService } from '~/app/services/errorManagement/vendor-error-code.service';

export function useErrorManagementPage() {
  const { t } = useTranslation('errorManagement');
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebouncedValue(searchValue, 300);
  const [manufacturerFilter, setManufacturerFilter] = useState<
    string | undefined
  >();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] =
    useState<VendorErrorCodeDrawerMode>('create');
  const [editingRecord, setEditingRecord] =
    useState<VendorErrorCodeResponseDto | null>(null);
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
  const [viewingRecord, setViewingRecord] =
    useState<VendorErrorCodeResponseDto | null>(null);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, manufacturerFilter, sortOrder]);

  const listQuery = useQuery({
    queryKey: [
      'vendor-error-codes',
      page,
      pageSize,
      debouncedSearch,
      manufacturerFilter,
      sortOrder,
    ],
    queryFn: () =>
      VendorErrorCodeService.list({
        page,
        limit: pageSize,
        ...(debouncedSearch.trim() ? { q: debouncedSearch.trim() } : {}),
        ...(manufacturerFilter ? { manufacturerId: manufacturerFilter } : {}),
        sortOrder,
      }),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (listQuery.isError) {
      message.error(t('messages.loadFailed'));
    }
  }, [listQuery.isError, message, t]);

  const manufacturersQuery = useQuery({
    queryKey: ['vendor-error-code-manufacturers'],
    queryFn: () => ManufacturerCatalogApi.list({ page: 1, limit: 1000 }),
  });

  const assetTypesQuery = useQuery({
    queryKey: ['vendor-error-code-asset-types'],
    queryFn: () =>
      assetTypesApi.assetTypeControllerFindAll({ page: 1, limit: 1000 }),
  });

  const manufacturerOptions = useMemo(
    () =>
      (manufacturersQuery.data?.data ?? []).map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [manufacturersQuery.data],
  );

  const assetTypeOptions = useMemo(
    () =>
      (assetTypesQuery.data?.data ?? []).map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [assetTypesQuery.data],
  );

  const invalidateCatalog = async () => {
    await queryClient.invalidateQueries({ queryKey: ['vendor-error-codes'] });
  };

  const invalidateUnresolved = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['unresolved-vendor-error-codes'],
    });
  };

  const invalidateBothLists = async () => {
    await Promise.all([invalidateCatalog(), invalidateUnresolved()]);
  };

  const createMutation = useMutation({
    mutationFn: (dto: CreateVendorErrorCodeRequestDto) =>
      VendorErrorCodeService.create(dto),
    onSuccess: async () => {
      message.success(t('messages.createSuccess'));
      setIsDrawerOpen(false);
      await invalidateCatalog();
    },
    onError: (error) => {
      message.error(
        isConflictError(error)
          ? t('messages.duplicate')
          : t('messages.createFailed'),
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: UpdateVendorErrorCodeRequestDto;
    }) => VendorErrorCodeService.update(id, dto),
    onSuccess: async () => {
      message.success(t('messages.updateSuccess'));
      setIsDrawerOpen(false);
      setEditingRecord(null);
      await invalidateCatalog();
    },
    onError: (error) => {
      message.error(
        isConflictError(error)
          ? t('messages.duplicate')
          : t('messages.updateFailed'),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => VendorErrorCodeService.delete(id),
    onError: () => message.error(t('messages.deleteFailed')),
  });

  const reopenMutation = useMutation({
    mutationFn: ({
      id,
      remark,
      expectedVersion,
    }: {
      id: string;
      remark: string;
      expectedVersion: number;
    }) => VendorErrorCodeService.reopen(id, { remark, expectedVersion }),
    onError: (error) => {
      message.error(
        isConflictError(error)
          ? t('messages.reopenConflict')
          : t('messages.reopenFailed'),
      );
    },
  });

  const handleDrawerSubmit = async (values: VendorErrorCodeFormValues) => {
    if (drawerMode === 'create') {
      await createMutation.mutateAsync(
        buildCreateRequestPayload(values) as CreateVendorErrorCodeRequestDto,
      );
      return;
    }

    if (!editingRecord) {
      return;
    }

    await updateMutation.mutateAsync({
      id: editingRecord.id,
      dto: buildUpdateRequestPayload(values) as UpdateVendorErrorCodeRequestDto,
    });
  };

  const handleDelete = (record: VendorErrorCodeResponseDto) => {
    const isQueueOriginated =
      record.source === VendorErrorSource.UNRESOLVED_QUEUE;
    Modal.confirm({
      title: isQueueOriginated
        ? t('messages.deleteBothConfirmTitle')
        : t('messages.deleteConfirmTitle'),
      content: isQueueOriginated
        ? t('messages.deleteBothConfirmDescription')
        : t('messages.deleteConfirmDescription'),
      okText: isQueueOriginated
        ? t('messages.deleteBothConfirmOk')
        : t('actions.delete'),
      okType: 'danger',
      onOk: async () => {
        await deleteMutation.mutateAsync(record.id);
        message.success(
          isQueueOriginated
            ? t('messages.deleteBothSuccess')
            : t('messages.deleteSuccess'),
        );
        if (isQueueOriginated) {
          await invalidateBothLists();
        } else {
          await invalidateCatalog();
        }
      },
    });
  };

  const handleReopen = (record: VendorErrorCodeResponseDto) => {
    if (record.source !== VendorErrorSource.UNRESOLVED_QUEUE) {
      return;
    }

    const linked = record.linkedUnresolved;
    if (
      linked == null ||
      linked.status !== UnresolvedVendorErrorCodeStatus.RESOLVED ||
      typeof linked.version !== 'number'
    ) {
      message.error(t('messages.reopenMissingLink'));
      return;
    }

    const expectedVersion = linked.version;
    let remark = '';
    Modal.confirm({
      title: t('messages.reopenConfirmTitle'),
      content: createElement(
        'div',
        null,
        createElement(
          'p',
          { style: { marginBottom: 12 } },
          t('messages.reopenConfirmDescription'),
        ),
        createElement(Input.TextArea, {
          rows: 3,
          placeholder: t('messages.reopenRemarkPlaceholder'),
          onChange: (event) => {
            remark = event.target.value;
          },
        }),
      ),
      okText: t('actions.reopen'),
      onOk: async () => {
        if (!remark.trim()) {
          message.error(t('messages.reopenRemarkRequired'));
          return Promise.reject(new Error('REMARK_REQUIRED'));
        }
        try {
          await reopenMutation.mutateAsync({
            id: record.id,
            remark: remark.trim(),
            expectedVersion,
          });
          message.success(t('messages.reopenSuccess'));
          await invalidateBothLists();
        } catch (error) {
          if (error instanceof Error && error.message === 'REMARK_REQUIRED') {
            return Promise.reject(error);
          }
          // reopenMutation.onError already toasts API failures
          return Promise.reject(error);
        }
      },
    });
  };

  return {
    rows: listQuery.data?.data ?? [],
    total: listQuery.data?.meta.total ?? 0,
    page,
    pageSize,
    searchValue,
    manufacturerFilter,
    manufacturerOptions,
    assetTypeOptions,
    sortOrder,
    isLoading: listQuery.isLoading,
    isDrawerOpen,
    drawerMode,
    editingRecord,
    isViewDrawerOpen,
    viewingRecord,
    isSubmitting:
      createMutation.isPending ||
      updateMutation.isPending ||
      deleteMutation.isPending ||
      reopenMutation.isPending,
    onSearchChange: (value: string) => {
      setSearchValue(value);
    },
    onManufacturerFilterChange: (value: string | undefined) => {
      setManufacturerFilter(value || undefined);
    },
    onPageChange: (nextPage: number, nextPageSize: number) => {
      setPage(nextPage);
      setPageSize(nextPageSize);
    },
    onToggleSortOrder: () => {
      setSortOrder((current) => (current === 'desc' ? 'asc' : 'desc'));
    },
    onAdd: () => {
      setIsViewDrawerOpen(false);
      setViewingRecord(null);
      setDrawerMode('create');
      setEditingRecord(null);
      setIsDrawerOpen(true);
    },
    onView: (record: VendorErrorCodeResponseDto) => {
      setIsDrawerOpen(false);
      setEditingRecord(null);
      setViewingRecord(record);
      setIsViewDrawerOpen(true);
    },
    onEdit: (record: VendorErrorCodeResponseDto) => {
      setIsViewDrawerOpen(false);
      setViewingRecord(null);
      setDrawerMode('edit');
      setEditingRecord(record);
      setIsDrawerOpen(true);
    },
    onDelete: handleDelete,
    onReopen: handleReopen,
    onDrawerClose: () => {
      setIsDrawerOpen(false);
      setEditingRecord(null);
    },
    onViewDrawerClose: () => {
      setIsViewDrawerOpen(false);
      setViewingRecord(null);
    },
    onDrawerSubmit: handleDrawerSubmit,
  };
}
